import { supabase } from "@/integrations/supabase/client";
import { resolveProjectIdFromUrl } from "@/lib/documentGenerator/exportHistory";
import {
  getLayout,
  safeFolderName,
  subPathForDocument,
  type FolderLayoutKey,
} from "@/lib/integrations/folderTree";
import type { AuthMode } from "@/lib/integrations/providers";

export type DestinationType = "sharepoint" | "onedrive" | "dalux" | "interaxo" | "webhook";
export type ProviderDestinationType = DestinationType;

export interface DocumentDestination {
  id: string;
  project_id: string;
  connector_type: DestinationType;
  /** Leverantörs-id från providers.ts (sharepoint, dalux, webhook …). */
  provider: ProviderDestinationType;
  auth_mode: AuthMode;
  status: "pending" | "connected" | "needs_reauth" | "disconnected";
  credential_hint: string | null;
  label: string | null;
  site_id: string | null;
  drive_id: string | null;
  root_path: string | null;
  remote_url: string | null;
  connected_user_id: string | null;
  connected_email: string | null;
  auto_push: boolean;
  is_active: boolean;
  last_sync_at: string | null;
  last_remote_id: string | null;
  last_remote_modified: string | null;
  folder_layout: { key: string; folders: string[] } | null;
  tree_created_at: string | null;
  created_at: string;
}


export interface SyncLogRow {
  id: string;
  destination_id: string | null;
  project_id: string;
  provider: string | null;
  document_type: string | null;
  doc_number: string | null;
  filename: string | null;
  remote_id: string | null;
  remote_version: string | null;
  direction: "push" | "pull";
  status: "success" | "error";
  error_message: string | null;
  synced_at: string;
}

/** connection_key är inte beviljad för klienten – vi väljer alltid explicita kolumner. */
const DEST_COLUMNS =
  "id, project_id, connector_type, provider, auth_mode, status, credential_hint, label, site_id, drive_id, root_path, remote_url, " +
  "connected_user_id, connected_email, auto_push, is_active, last_sync_at, " +
  "last_remote_id, last_remote_modified, folder_layout, tree_created_at, created_at";

const MICROSOFT_FUNCTION = "microsoft-destination";
const GENERIC_FUNCTION = "generic-destination";
const GENERIC_PROVIDERS = new Set<ProviderDestinationType>(["dalux", "interaxo", "webhook"]);
const functionForDestination = (destination: Pick<DocumentDestination, "provider">) =>
  GENERIC_PROVIDERS.has(destination.provider) ? GENERIC_FUNCTION : MICROSOFT_FUNCTION;
const invokeBase = (functionName: string) =>
  `${import.meta.env.VITE_SUPABASE_URL ?? ""}/functions/v1/${functionName}`;

export const listDestinations = async (projectId: string | null): Promise<DocumentDestination[]> => {
  let query = supabase.from("document_destinations").select(DEST_COLUMNS).order("created_at", { ascending: false });
  if (projectId) query = query.eq("project_id", projectId);
  const { data, error } = await query;
  if (error) return [];
  return (data || []) as unknown as DocumentDestination[];
};

export const createDestination = async (input: {
  projectId: string;
  connectorType: DestinationType;
  provider?: ProviderDestinationType;
  authMode?: AuthMode;
  label?: string | null;
}): Promise<DocumentDestination | null> => {
  const provider = input.provider ?? input.connectorType;
  const authMode = input.authMode ?? "oauth";
  const { data, error } = await supabase
    .from("document_destinations")
    .insert({
      project_id: input.projectId,
      connector_type: input.connectorType,
      provider,
      auth_mode: authMode,
      label: input.label ?? null,
    })
    .select(DEST_COLUMNS)
    .single();
  if (error || !data) return null;
  return data as unknown as DocumentDestination;
};

export const updateDestination = async (
  id: string,
  patch: Partial<
    Pick<
      DocumentDestination,
      "auto_push" | "is_active" | "label" | "root_path" | "remote_url" | "folder_layout"
    >
  >
): Promise<boolean> => {
  const { error } = await supabase.from("document_destinations").update(patch).eq("id", id);
  return !error;
};


export const deleteDestination = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from("document_destinations").delete().eq("id", id);
  return !error;
};

export const listSyncLog = async (projectId: string | null): Promise<SyncLogRow[]> => {
  let query = supabase
    .from("document_sync_log")
    .select("id, destination_id, project_id, provider, document_type, doc_number, filename, remote_id, remote_version, direction, status, error_message, synced_at")
    .order("synced_at", { ascending: false })
    .limit(50);
  if (projectId) query = query.eq("project_id", projectId);
  const { data, error } = await query;
  if (error) return [];
  return (data || []) as SyncLogRow[];
};

export const logSync = async (input: {
  projectId: string;
  destinationId?: string | null;
  provider?: string | null;
  documentType?: string | null;
  docNumber?: string | null;
  filename?: string | null;
  remoteId?: string | null;
  remoteVersion?: string | null;
  direction: "push" | "pull";
  status: "success" | "error";
  errorMessage?: string | null;
}): Promise<void> => {
  await supabase.from("document_sync_log").insert({
    project_id: input.projectId,
    destination_id: input.destinationId ?? null,
    provider: input.provider ?? null,
    document_type: input.documentType ?? null,
    doc_number: input.docNumber ?? null,
    filename: input.filename ?? null,
    remote_id: input.remoteId ?? null,
    remote_version: input.remoteVersion ?? null,
    direction: input.direction,
    status: input.status,
    error_message: input.errorMessage ?? null,
  });
};

/** Är en Microsoft-destination ansluten för projektet? Hemligheten är aldrig läsbar i klienten. */
export const hasConnectedDestination = async (projectId: string): Promise<boolean> => {
  const { data } = await supabase
    .from("document_destinations")
    .select("id")
    .eq("project_id", projectId)
    .eq("has_connection", true)
    .limit(1);
  return (data?.length ?? 0) > 0;
};

/**
 * Skapar mappträdet i SharePoint/OneDrive för destinationen.
 * `basePath` är oftast projektnamnet så att varje projekt får en egen rotmapp.
 */
export const createFolderTree = async (input: {
  destinationId: string;
  layout: FolderLayoutKey;
  basePath?: string | null;
}): Promise<{ ok: boolean; created: string[]; error?: string }> => {
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  if (!token) return { ok: false, created: [], error: "Du måste vara inloggad" };

  const layout = getLayout(input.layout);
  const res = await fetch(invokeBase(MICROSOFT_FUNCTION), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-client-info": "supabase-js/2",
    },
    body: JSON.stringify({
      action: "create-folder-tree",
      destinationId: input.destinationId,
      basePath: input.basePath ? safeFolderName(input.basePath) : null,
      layout: layout.key,
      folders: layout.folders.map((f) => f.path),
    }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    return { ok: false, created: [], error: data?.error ? String(data.error) : `HTTP ${res.status}` };
  }
  return { ok: true, created: (data?.created ?? []) as string[] };
};

/** Skickar ett Word-dokument till projektets aktiva auto-push-destination (best effort). */
export const pushDocumentToDestination = async (
  filename: string,
  blob: Blob,
  meta: { projectId?: string | null; documentType?: string | null; docNumber?: string | null }
): Promise<void> => {
  try {
    const projectId = meta.projectId ?? resolveProjectIdFromUrl();
    if (!projectId) return;

    const { data: auth } = await supabase.auth.getUser();
    const token = auth?.user ? (await supabase.auth.getSession()).data.session?.access_token : null;
    if (!token) return;

    const destinations = await listDestinations(projectId);
    const target = destinations.find((d) => d.is_active && d.auto_push);
    if (!target) return;

    const base64 = await blobToBase64(blob);
    const subPath = target.folder_layout?.key
      ? subPathForDocument(target.folder_layout.key, meta.docNumber, meta.documentType)
      : "";

    const res = await fetch(invokeBase(functionForDestination(target)), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-client-info": "supabase-js/2",
      },
      body: JSON.stringify({
        action: "push",
        destinationId: target.id,
        projectId,
        filename,
        documentType: meta.documentType ?? null,
        docNumber: meta.docNumber ?? null,
         subPath,
         contentBase64: base64,
         file_base64: base64,
       }),
     });

     if (!res.ok) {
      const err = await res.json().catch(() => null);
      await logSync({
        projectId,
        destinationId: target.id,
        documentType: meta.documentType ?? null,
        docNumber: meta.docNumber ?? null,
        filename,
        direction: "push",
        status: "error",
        errorMessage: err?.error ? String(err.error).slice(0, 500) : `HTTP ${res.status}`,
      });
      return;
    }

    const data = await res.json();
    await logSync({
      projectId,
      destinationId: target.id,
      documentType: meta.documentType ?? null,
      docNumber: meta.docNumber ?? null,
      filename,
      remoteId: data.remote_id ?? null,
      remoteVersion: data.remote_version ?? null,
      direction: "push",
      status: "success",
    });
  } catch (err) {
    console.warn("Dokumentdestination push misslyckades", err);
  }
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.readAsDataURL(blob);
  });

/**
 * Skickar en godtycklig fil (t.ex. en säkerhetskopia som ZIP) till en vald
 * destination. Till skillnad från auto-push kräver detta inte att destinationen
 * har auto_push påslaget – användaren väljer själv var kopian ska ligga.
 */
export const pushFileToDestination = async (input: {
  destinationId: string;
  projectId: string;
  filename: string;
  blob: Blob;
  subPath?: string;
  documentType?: string | null;
  docNumber?: string | null;
}): Promise<{ ok: boolean; error?: string }> => {
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  if (!token) return { ok: false, error: "Du måste vara inloggad" };

  try {
    const base64 = await blobToBase64(input.blob);
    const destinations = await listDestinations(input.projectId);
    const target = destinations.find((destination) => destination.id === input.destinationId);
    if (!target) return { ok: false, error: "Destinationen hittades inte" };
    const res = await fetch(invokeBase(functionForDestination(target)), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-client-info": "supabase-js/2",
      },
      body: JSON.stringify({
        action: "push",
        destinationId: input.destinationId,
        projectId: input.projectId,
        filename: input.filename,
        documentType: input.documentType ?? "backup",
         docNumber: input.docNumber ?? null,
         subPath: input.subPath ?? "",
         contentBase64: base64,
         file_base64: base64,
       }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const message = err?.error ? String(err.error).slice(0, 500) : `HTTP ${res.status}`;
      await logSync({
        projectId: input.projectId,
        destinationId: input.destinationId,
        documentType: input.documentType ?? "backup",
        docNumber: input.docNumber ?? null,
        filename: input.filename,
        direction: "push",
        status: "error",
        errorMessage: message,
      });
      return { ok: false, error: message };
    }

    const data = await res.json().catch(() => ({}));
    await logSync({
      projectId: input.projectId,
      destinationId: input.destinationId,
      documentType: input.documentType ?? "backup",
      docNumber: input.docNumber ?? null,
      filename: input.filename,
      remoteId: data?.remote_id ?? null,
      remoteVersion: data?.remote_version ?? null,
      direction: "push",
      status: "success",
    });
    return { ok: true };
  } catch (err) {
    const message = (err as Error).message;
    await logSync({
      projectId: input.projectId,
      destinationId: input.destinationId,
      documentType: input.documentType ?? "backup",
      filename: input.filename,
      direction: "push",
      status: "error",
      errorMessage: message.slice(0, 500),
    });
    return { ok: false, error: message };
  }
};
